import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, PlayCircle, Star, Gift } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { Hero } from './components/Hero';
import { ProductVideo } from './components/ProductVideo';
import { ProductCarousel } from './components/ProductCarousel';
import { PricingPacks } from './components/PricingPacks';
import { Testimonials } from './components/Testimonials';
import { useScreenInit } from './useScreenInit.js';
export function App() {
  const screenInit = useScreenInit();
  useEffect(() => {
    if (screenInit?.section) {
      const element = document.getElementById(screenInit.section);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [screenInit]);
  // Initialize Meta Pixel
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1671946790698960');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1671946790698960&ev=PageView&noscript=1" />`;
    document.head.appendChild(noscript);
    return () => {
      // Cleanup is generally not needed for the pixel script itself
    };
  }, []);
  return <div className="min-h-screen bg-[#fdfaf6] font-sans selection:bg-[#eaddca]" data-id="element-0">
      <div className="absolute top-0 left-0 right-0 z-40" data-id="element-1">
        <CountdownTimer data-id="element-2" />
      </div>

      <Hero data-id="element-3" />

      {/* Pain Section */}
      <section className="py-16 px-4 bg-white pt-[0px] pb-[20px]" data-id="element-4">
        <div className="max-w-5xl mx-auto" data-id="element-5">
          <h2 className="text-3xl md:text-5xl font-black text-red-700 text-center mb-12 uppercase tracking-tighter" data-id="element-6">
            ❌ ¿TE SENTÍS IDENTIFICADO?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7">
            {[{
            emoji: '💸',
            title: 'INGRESOS QUE NO ALCANZAN',
            desc: 'Dependés de un sueldo que cada mes rinde menos y necesitás otra fuente de ingresos.'
          }, {
            emoji: '🚫',
            title: 'NO SABÉS POR DÓNDE EMPEZAR',
            desc: 'Querés aprender un oficio, pero no encontrás una guía clara y completa.'
          }, {
            emoji: '😞',
            title: 'POCAS OPORTUNIDADES',
            desc: 'Sentís que pasan los meses y seguís sin adquirir una habilidad que te permita crecer.'
          }, {
            emoji: '⏳',
            title: 'DEPENDER DE UN EMPLEO',
            desc: 'Tu ingreso depende de un jefe o de conseguir trabajo, sin poder manejar tus propios horarios.'
          }, {
            emoji: '📉',
            title: 'DEJAR PASAR OPORTUNIDADES',
            desc: 'Mientras otros aprenden un oficio y generan ingresos, vos seguís esperando el momento ideal para empezar.'
          }].map((item, i) => <div key={i} className="p-6 bg-red-50 rounded-2xl border-2 border-red-200 hover:border-red-400 hover:shadow-xl transition-all duration-300 group" data-id="element-8">
                <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform" data-id="element-9">
                  {item.emoji}
                </div>
                <h3 className="text-xl font-black text-red-800 mb-3 uppercase tracking-tight leading-none" data-id="element-10">
                  {item.title}
                </h3>
                <p className="text-sm text-red-900 font-bold leading-relaxed opacity-80" data-id="element-11">
                  {item.desc}
                </p>
              </div>)}
          </div>

          {/* Quick Solutions Section */}
          <div className="mt-24" data-id="element-12">
            <h2 className="text-3xl md:text-5xl font-black text-green-700 text-center mb-12 uppercase tracking-tight leading-none" data-id="element-13">
              🔐 ¿QUERÉS EMPEZAR EN CERRAJERÍA?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-14">
              {[{
              emoji: '💰',
              title: 'GENERÁ INGRESOS',
              desc: 'Aprendé un oficio que te permite ofrecer servicios para hogares y vehículos.'
            }, {
              emoji: '🚗',
              title: 'HOGAR + AUTOMOTRIZ',
              desc: 'Capacitate en dos áreas con alta demanda y ampliá las oportunidades de trabajo.'
            }, {
              emoji: '📚',
              title: 'APRENDÉ DESDE CERO',
              desc: 'Explicaciones paso a paso, incluso si nunca trabajaste en cerrajería.'
            }, {
              emoji: '🛠️',
              title: 'SUMÁ MÁS SERVICIOS',
              desc: 'Mientras más soluciones podés ofrecer, más clientes podés conseguir.'
            }, {
              emoji: '📈',
              title: 'EMPEZÁ A TU RITMO',
              desc: 'Acceso inmediato y de por vida para avanzar cuando vos quieras.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} whileHover={{
              scale: 1.05,
              backgroundColor: '#f0fdf4'
            }} className="p-8 bg-white rounded-[2rem] border-2 border-green-100 shadow-lg flex flex-col items-center text-center group transition-all relative overflow-hidden" data-id="element-15">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" data-id="element-16" />

                  <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-5xl mb-6 transform group-hover:scale-110 transition-transform relative z-10" data-id="element-17">
                    {item.emoji}
                  </motion.div>
                  <h3 className="text-xl font-black text-green-800 mb-4 uppercase tracking-tight relative z-10 leading-tight" data-id="element-18">
                    {item.title}
                  </h3>
                  <p className="text-sm text-green-900 font-bold leading-relaxed relative z-10 opacity-90" data-id="element-19">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section id="bonuses" className="py-20 px-4 bg-[#fdfaf6] pt-[0px] pb-[0px]" data-id="element-20">
        <div className="max-w-6xl mx-auto" data-id="element-21">
          <motion.h2 initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl font-black text-[#5d4037] text-center mb-16 uppercase tracking-tight flex flex-col md:flex-row items-center justify-center gap-4" data-id="element-22">
            <motion.div animate={{
            rotate: [0, -10, 10, -10, 10, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatDelay: 2
          }} data-id="element-23">
              <Gift className="w-10 h-10 md:w-12 md:h-12 text-[#d4af37]" data-id="element-24" />
            </motion.div>
            <span data-id="element-25">
              SOLO POR HOY{' '}
              <span className="text-[#d4af37]" data-id="element-26">
                {new Date().toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long'
              }).toUpperCase()}
              </span>{' '}
              SE LLEVAN ESTOS BONOS DE REGALOS
            </span>
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-27">
            {[{
            title: 'Lockpicking Técnico',
            desc: 'Domina el arte de la apertura técnica con esta guía detallada.',
            img: 'https://cdn.magicpatterns.com/uploads/o2QTJx8A2nCAK8MNVu4hAS/bonus_1_lockpicking.webp'
          }, {
            title: 'Curso Rápido de Apertura',
            desc: 'Aprende los fundamentos esenciales en tiempo récord para empezar ya.',
            img: 'https://cdn.magicpatterns.com/uploads/bZvvUizEhdAJqGJYjzv9HZ/bonus_2_curso_rapido.webp'
          }, {
            title: 'La Biblia del Lockpicking',
            desc: 'El compendio definitivo con todos los secretos de la cerrajería moderna.',
            img: 'https://cdn.magicpatterns.com/uploads/coKSq1p7ZEXj1kKTZPXScK/Bonus_3_la_biblia_del_lock.webp'
          }, {
            title: 'Manual de Cerrojos',
            desc: 'Especialízate en la apertura y reparación de cerrojos de alta seguridad.',
            img: 'https://cdn.magicpatterns.com/uploads/rmgHWzZD1WiKsEKuMNaHMt/bonuis_4_cerrojos.webp'
          }, {
            title: 'Manual de Cerraduras',
            desc: 'Guía práctica paso a paso para enfrentar cualquier tipo de cerradura.',
            img: 'https://cdn.magicpatterns.com/uploads/5Uix8a1uvakTb6MP8mM7FX/bonus_5_manual_para_abrir_cerraduras.webp'
          }, {
            title: 'Clonación de Llaves',
            desc: 'Todo lo que necesitás saber para clonar y programar llaves modernas.',
            img: 'https://cdn.magicpatterns.com/uploads/eu86xHSaarbnXDJ3KJfYjo/Bonus_1_clonacion_de_llaves.webp'
          }, {
            title: 'Duplicar Llave Automotor',
            desc: 'Guía práctica y profesional para duplicar llaves de autos con precisión.',
            img: 'https://cdn.magicpatterns.com/uploads/m498VCNr6gJQACocjpXNMj/bonus_2_duplicar_llave_de_automotor.webp'
          }, {
            title: 'Programar Llave Automotor',
            desc: 'Consejos de expertos para la programación de sistemas electrónicos vehiculares.',
            img: 'https://cdn.magicpatterns.com/uploads/3gV6HptByaeQoxRtqTWQbT/bonus_3_programar_llave.webp'
          }, {
            title: 'Transponder General',
            desc: 'Conocé a fondo la tecnología transponder y su aplicación en seguridad.',
            img: 'https://cdn.magicpatterns.com/uploads/tsSKELZnpbxK1xS1NbVkRg/bonus_4_transponder_general.webp'
          }, {
            title: 'Guía Varios',
            desc: 'Técnicas especializadas y soluciones de bypass de vanguardia.',
            img: 'https://cdn.magicpatterns.com/uploads/1f8e7LwNbqNZ5SH2AjbGTj/bonus_varios.webp'
          }].map((bonus, i) => <motion.div key={i} whileHover={{
            y: -10
          }} className="bg-white rounded-3xl shadow-xl border-b-8 border-[#d4af37] overflow-hidden flex flex-col h-full" data-id="element-28">
                <div className="relative aspect-square overflow-hidden" data-id="element-29">
                  <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-30" />
                  <div className="absolute top-4 right-4 bg-[#d4af37] text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg" data-id="element-31">
                    BONUS {i + 1}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-grow text-center" data-id="element-32">
                  <h3 className="font-bold text-[#d4af37] text-lg leading-tight mb-2" data-id="element-33">
                    {bonus.title}
                  </h3>
                  <p className="text-black text-xs mb-4 flex-grow" data-id="element-34">
                    {bonus.desc}
                  </p>
                  <div className="mt-auto pt-4" data-id="element-35">
                    <span className="inline-block bg-[#d4af37] text-white text-xs font-black px-4 py-1.5 rounded-full shadow-md" data-id="element-36">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ProductCarousel data-id="element-37" />

      <div id="pricing" data-id="element-38">
        <PricingPacks data-id="element-39" />
      </div>

      <ProductVideo data-id="element-40" />

      <Testimonials data-id="element-41" />

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white pt-[0px] pb-[0px]" data-id="element-42">
        <div className="max-w-4xl mx-auto" data-id="element-43">
          <h2 className="text-3xl md:text-4xl font-bold text-[#5d4037] text-center mb-12" data-id="element-44">
            ¿Qué vas a lograr con este material?
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-45">
            {['Aprender desde cero', 'Tener una habilidad práctica', 'Estudiar a tu ritmo', 'Acceso inmediato', 'Material organizado y fácil de seguir', 'Ideal para sumar una salida laboral'].map((benefit, i) => <div key={i} className="flex items-center gap-4 p-4 bg-[#fdfaf6] rounded-xl border border-[#eaddca]" data-id="element-46">
                <CheckCircle2 className="text-green-500 w-6 h-6 shrink-0" data-id="element-47" />
                <span className="text-[#5d4037] font-medium" data-id="element-48">{benefit}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section id="guarantee" className="py-20 px-4 bg-[#fdfaf6] pt-[40px] pb-[40px]" data-id="element-49">
        <div className="max-w-3xl mx-auto text-center bg-white p-12 rounded-[3rem] shadow-xl border-2 border-[#eaddca]" data-id="element-50">
          <ShieldCheck className="w-20 h-20 text-[#d4af37] mx-auto mb-6" data-id="element-51" />
          <h2 className="text-3xl font-bold text-[#5d4037] mb-4" data-id="element-52">
            Compra segura
          </h2>
          <p className="text-xl text-[#8b6b61] mb-8" data-id="element-53">
            Recibís el acceso de forma inmediata y tenés garantía de 7 días.
          </p>
          <div className="flex justify-center gap-4" data-id="element-54">
            <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-8 opacity-50" data-id="element-55" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-8 opacity-50" data-id="element-56" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-[#5d4037] text-white text-center" data-id="element-57">
        <div className="max-w-3xl mx-auto" data-id="element-58">
          <h2 className="text-4xl font-bold mb-8" data-id="element-59">
            ¿Listo para empezar tu nuevo oficio?
          </h2>
          <button onClick={() => {
          const target = document.getElementById('pricing');
          if (!target) return;
          const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
          const startPosition = window.pageYOffset;
          const distance = targetPosition - startPosition;
          const duration = 2000;
          let start: number | null = null;
          const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = timestamp - start;
            const percentage = Math.min(progress / duration, 1);
            const easing = percentage < 0.5 ? 4 * percentage * percentage * percentage : 1 - Math.pow(-2 * percentage + 2, 3) / 2;
            window.scrollTo(0, startPosition + distance * easing);
            if (progress < duration) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        }} className="bg-[#d4af37] hover:bg-[#c4a030] text-white font-bold py-6 px-12 rounded-2xl text-2xl shadow-2xl transition-all transform hover:scale-105 active:scale-95 mb-8" data-id="element-60">
            ACCEDER AHORA
          </button>
          <p className="text-white/70 flex items-center justify-center gap-2" data-id="element-61">
            <Star className="w-4 h-4 fill-current" data-id="element-62" /> Más de 1,500 alumnos ya
            empezaron
          </p>
        </div>
      </section>

      <footer className="py-8 text-center text-[#8b6b61] text-sm border-t border-[#eaddca]" data-id="element-63">
        <p data-id="element-64">© 2024 Cerrajería Profesional. Todos los derechos reservados.</p>
      </footer>
    </div>;
}